import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import { requestNotificationPermission } from '../../services/notificationService';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    sound: true,
    newMessage: true,
    mentions: true,
    status: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { data, error } = await supabase
        .from('user_settings')
        .select('notification_settings')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      if (data?.notification_settings) {
        setSettings(data.notification_settings);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    try {
      if (key === 'enabled') {
        const permissionGranted = await requestNotificationPermission();
        if (!permissionGranted) {
          throw new Error('Permission de notification refusée');
        }
      }

      const newSettings = {
        ...settings,
        [key]: !settings[key],
      };

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          notification_settings: newSettings,
          updated_at: new Date(),
        });

      if (error) throw error;

      setSettings(newSettings);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setError('Erreur lors de la mise à jour des paramètres');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <Switch.Group as="div" className="flex items-center justify-between">
      <div className="flex-grow flex flex-col">
        <Switch.Label as="span" className="text-sm font-medium text-gray-900" passive>
          {label}
        </Switch.Label>
        {description && (
          <Switch.Description as="span" className="text-sm text-gray-500">
            {description}
          </Switch.Description>
        )}
      </div>
      <Switch
        checked={enabled}
        onChange={onChange}
        className={`${
          enabled ? 'bg-primary-600' : 'bg-gray-200'
        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
      >
        <span
          aria-hidden="true"
          className={`${
            enabled ? 'translate-x-5' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </Switch>
    </Switch.Group>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="flex items-center space-x-4 pb-6">
        {settings.enabled ? (
          <BellIcon className="h-8 w-8 text-primary-500" />
        ) : (
          <BellSlashIcon className="h-8 w-8 text-gray-400" />
        )}
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Notifications
          </h3>
          <p className="text-sm text-gray-500">
            Gérez vos préférences de notification
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <ToggleSwitch
          enabled={settings.enabled}
          onChange={() => handleToggle('enabled')}
          label="Activer les notifications"
          description="Recevez des notifications pour les nouveaux messages et les mises à jour importantes"
        />

        <div className="space-y-4 ml-6">
          <ToggleSwitch
            enabled={settings.enabled && settings.sound}
            onChange={() => settings.enabled && handleToggle('sound')}
            label="Son"
            description="Jouer un son lors de la réception d'une notification"
          />

          <ToggleSwitch
            enabled={settings.enabled && settings.newMessage}
            onChange={() => settings.enabled && handleToggle('newMessage')}
            label="Nouveaux messages"
            description="Notifications pour les nouveaux messages reçus"
          />

          <ToggleSwitch
            enabled={settings.enabled && settings.mentions}
            onChange={() => settings.enabled && handleToggle('mentions')}
            label="Mentions"
            description="Notifications lorsque vous êtes mentionné dans un message"
          />

          <ToggleSwitch
            enabled={settings.enabled && settings.status}
            onChange={() => settings.enabled && handleToggle('status')}
            label="Mises à jour de statut"
            description="Notifications pour les changements de statut de connexion"
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
